import { parse } from "tldts";

/**
 * Aplica a regra de canonicalização de host da plataforma:
 * - domínio raiz (apex) → versão `www` (espelha o redirect 301 raiz→www);
 * - subdomínio (incluindo `www.`, `loja.`, previews) → mantém exatamente;
 * - IP/localhost/host vazio → mantém como está (preview/dev).
 *
 * A distinção apex×subdomínio vem da Public Suffix List embutida no `tldts`
 * (evita manter uma lista de TLDs compostos à mão). O `parse` já devolve o
 * `hostname` normalizado (sem protocolo, sem barra final, minúsculo, sem porta,
 * sem ponto final do FQDN e descartando caminho colado no host).
 */
export function normalizeHostToWww(host: string | null | undefined): string {
  const { hostname, subdomain } = parse(host ?? "");

  if (!hostname) {
    return "";
  }

  // subdomain "" = apex (aplica www); "www"/"loja"/preview = subdomínio (mantém);
  // null = localhost/IP (mantém).
  return subdomain === "" ? `www.${hostname}` : hostname;
}

/**
 * Remove query string e fragmento de um caminho e limpa barras nas pontas,
 * retornando o caminho "cru" (ex.: `sobre`, `novos/abc123` ou `""` para a home).
 */
function cleanPagePath(path: string | null | undefined): string {
  if (!path) {
    return "";
  }
  const withoutQuery = path.split(/[?#]/)[0];
  return withoutQuery.replace(/^\/+/, "").replace(/\/+$/, "");
}

/**
 * Junta o prefixo do canal com o caminho da página. Compartilhado com o
 * `sitemap.ts` para garantir que canonical e sitemap gerem URLs iguais:
 * - caminho vazio → prefixo do canal, ou `/` na home da raiz (única barra final).
 */
export function joinCanonicalPath(prefix: string, path: string): string {
  if (!path) {
    return prefix || "/";
  }
  return `${prefix}/${path}`;
}

/**
 * Constrói o prefixo do slug do canal (`""` ou `/slug-limpo`). Compartilhado
 * entre canonical e sitemap para que ambos normalizem o slug da mesma forma.
 */
export function buildChannelPrefix(channelSlug: string | null | undefined): string {
  const cleanSlug = cleanPagePath(channelSlug);
  return cleanSlug ? `/${cleanSlug}` : "";
}

export interface BuildCanonicalUrlParams {
  /** Domínio apontado do canal (channel.domain). Tem prioridade sobre `host`. */
  domain?: string | null;
  /** Host da request, usado como fallback quando o canal não tem domínio. */
  host?: string | null;
  /** Slug do canal para sites publicados em subcaminho (ex.: `ford/seminovos`). */
  channelSlug?: string | null;
  /** Caminho da página (ex.: `/`, `/sobre`, `/novos/abc123`). */
  path?: string | null;
}

/**
 * Constrói a URL canônica auto-referente de uma página, seguindo as regras de
 * SEO da plataforma: sempre `https://`, host canonicalizado (apex→www), com o
 * prefixo do slug do canal, sem query params e sem barra final (exceto a home
 * da raiz). Retorna `""` quando não há host resolvível.
 */
export function buildCanonicalUrl({
  domain,
  host,
  channelSlug,
  path,
}: BuildCanonicalUrlParams): string {
  const normalizedHost = normalizeHostToWww(domain || host);
  if (!normalizedHost) {
    return "";
  }

  const sitePrefix = buildChannelPrefix(channelSlug);
  const combined = joinCanonicalPath(sitePrefix, cleanPagePath(path));

  return `https://${normalizedHost}${combined}`;
}
